import React, { useEffect, useRef, useState } from 'react';
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

/* Animated counter hook */
function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const num = parseFloat(target) || 0;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(num * ease);
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/* Single animated stat card */
const StatCard = ({ label, rawValue, Icon, ArrowIcon, gradient, dark, delay }) => {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const animated = useCountUp(visible ? rawValue : 0, 1100);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const { fmt } = useCurrency();
  const formattedAnimated = fmt(animated);

  const textColor = dark ? '#5a4000' : '#ffffff';
  const subColor  = dark ? '#7a5a00' : 'rgba(255,255,255,0.80)';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: gradient,
        borderRadius: '1.25rem',
        padding: '1.5rem',
        boxShadow: hovered
          ? '0 20px 40px rgba(196,69,105,0.28), 0 4px 16px rgba(0,0,0,0.10)'
          : '0 4px 18px rgba(196,69,105,0.14)',
        transform: visible
          ? hovered ? 'translateY(-6px) scale(1.025)' : 'translateY(0) scale(1)'
          : 'translateY(24px) scale(0.97)',
        opacity: visible ? 1 : 0,
        transition: `transform 0.45s cubic-bezier(0.34,1.56,0.64,1) ${delay}ms,
                     opacity 0.45s ease ${delay}ms,
                     box-shadow 0.3s ease`,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Decorative circles */}
      <div style={{position:'absolute',top:'-20px',right:'-20px',width:'90px',height:'90px',borderRadius:'50%',background:'rgba(255,255,255,0.10)',transition:'transform 0.4s ease',transform:hovered?'scale(1.3)':'scale(1)'}} />
      <div style={{position:'absolute',bottom:'-28px',left:'-10px',width:'70px',height:'70px',borderRadius:'50%',background:'rgba(255,255,255,0.07)',transition:'transform 0.4s ease',transform:hovered?'scale(1.4)':'scale(1)'}} />
      <div style={{position:'absolute',top:'50%',right:'-40px',width:'120px',height:'120px',borderRadius:'50%',background:'rgba(255,255,255,0.05)',transform:`translateY(-50%) ${hovered?'scale(1.2)':'scale(1)'}`,transition:'transform 0.5s ease'}} />

      <div style={{position:'relative'}}>
        {/* Top row */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <div style={{
              width:'36px',height:'36px',borderRadius:'10px',
              background:'rgba(255,255,255,0.20)',
              display:'flex',alignItems:'center',justifyContent:'center',
              transition:'transform 0.3s ease',
              transform: hovered ? 'rotate(-8deg) scale(1.12)' : 'rotate(0) scale(1)',
            }}>
              <Icon size={18} color={textColor} strokeWidth={2.2} />
            </div>
            <span style={{fontSize:'0.8rem',fontWeight:600,color:subColor,letterSpacing:'0.02em'}}>{label}</span>
          </div>
          {ArrowIcon && (
            <div style={{
              background:'rgba(255,255,255,0.18)',
              borderRadius:'8px',padding:'4px',
              transition:'transform 0.3s ease',
              transform: hovered ? 'translateX(3px) translateY(-3px)' : 'none',
            }}>
              <ArrowIcon size={14} color={textColor} strokeWidth={2.5} />
            </div>
          )}
        </div>

        {/* Value */}
        <div style={{
          fontSize:'1.65rem',fontWeight:800,color:textColor,
          letterSpacing:'-0.02em',lineHeight:1.1,
          transition:'transform 0.3s ease',
          transform: hovered ? 'scale(1.04)' : 'scale(1)',
          transformOrigin: 'left center',
        }}>
          {formattedAnimated}
        </div>

        {/* Shimmer bar */}
        <div style={{
          marginTop:'14px',height:'3px',borderRadius:'99px',
          background:'rgba(255,255,255,0.20)',overflow:'hidden',
        }}>
          <div style={{
            height:'100%',borderRadius:'99px',
            background:'rgba(255,255,255,0.60)',
            width: visible ? '100%' : '0%',
            transition:`width 1.2s cubic-bezier(0.4,0,0.2,1) ${delay + 200}ms`,
          }} />
        </div>
      </div>
    </div>
  );
};

const StatCards = ({ balance }) => {
  if (!balance) return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
      {[1, 2, 3].map(i => (
        <div key={i} className="rounded-2xl p-6 shadow-sm animate-pulse" style={{background:'#fce0e8'}}>
          <div style={{height:'12px',borderRadius:'6px',background:'rgba(196,69,105,0.15)',width:'60%',marginBottom:'16px'}} />
          <div style={{height:'28px',borderRadius:'6px',background:'rgba(196,69,105,0.15)',width:'80%'}} />
          <div style={{height:'3px',borderRadius:'99px',background:'rgba(196,69,105,0.10)',marginTop:'18px'}} />
        </div>
      ))}
    </div>
  );

  const cards = [
    {
      label: 'Current Balance',
      rawValue: balance.current_balance,
      Icon: Wallet,
      ArrowIcon: null,
      gradient: 'linear-gradient(135deg, #c44569 0%, #cf6a87 100%)',
      delay: 0,
    },
    {
      label: 'Total Income',
      rawValue: balance.total_income,
      Icon: TrendingUp,
      ArrowIcon: ArrowUpRight,
      gradient: 'linear-gradient(135deg, #f5cd79 0%, #f7d794 100%)',
      dark: true,
      delay: 120,
    },
    {
      label: 'Total Expense',
      rawValue: balance.total_expense,
      Icon: TrendingDown,
      ArrowIcon: ArrowDownRight,
      gradient: 'linear-gradient(135deg, #cf6a87 0%, #f5cd79 100%)',
      delay: 240,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
      {cards.map(c => <StatCard key={c.label} {...c} />)}
    </div>
  );
};

export default StatCards;
